import { useState, useEffect, MouseEvent, KeyboardEvent } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useTranslation } from '@/i18n'

interface RenameDialogProps {
  children: React.ReactNode
  currentName: string
  onConfirmRename: (newName: string) => void
}

const RenameDialog = ({
  children,
  currentName,
  onConfirmRename,
}: RenameDialogProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [newName, setNewName] = useState(currentName)

  // Reset the name when dialog opens or currentName changes
  useEffect(() => {
    if (open) {
      setNewName(currentName)
    }
  }, [open, currentName])

  const handleConfirm = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    const trimmedName = newName.trim()
    if (trimmedName && trimmedName !== currentName) {
      onConfirmRename(trimmedName)
    }
    setOpen(false)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const trimmedName = newName.trim()
    if (e.key === 'Enter' && trimmedName && trimmedName !== currentName) {
      e.preventDefault()
      onConfirmRename(trimmedName)
      setOpen(false)
    }
  }

  const isValid = newName.trim() && newName.trim() !== currentName

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild onClick={(e) => e.stopPropagation()}>
        {children}
      </DialogTrigger>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t('common:rename_dialog.title')}</DialogTitle>
          <DialogDescription>
            {t('common:rename_dialog.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('common:rename_dialog.placeholder')}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation()
              setOpen(false)
            }}
          >
            {t('common:rename_dialog.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!isValid}>
            {t('common:rename_dialog.rename')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default RenameDialog
